import {
  BooleanInput,
  Create,
  Edit,
  FileInput,
  Resource,
  TextInput,
  useGetOne,
  useInput,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { type FieldValues } from "react-hook-form";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Column } from "../misc/Column";
import Box from "@mui/material/Box";
import { Button, IconButton, Menu, MenuItem, Popover } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Row } from "../misc/Row";
import { Form2 } from "../misc/Form2";
import { grants } from "../backoffice.access_control";
import { AccessDenied } from "../misc/AccessDenied";
import { type DrivingSchool } from "../providers/drivingSchoolsProvider";
import { type DrivingSchoolAutoForm } from "../providers/autoFormsProvider";
import { SaveButton2 } from "../buttons/SaveButton2";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { DefaultList } from "../misc/DefaultList";
import { formatFileSize } from "../utils/string";
import { useCallback, useState } from "react";
import { useDialog } from "../hooks/useDialog";
import { AutoFormTemplateDialog } from "../details/drivingSchool/AutoFormTemplateDialog";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMenu } from "../hooks/useMenu";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateAutoFormForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    { enabled: !!drivingSchoolId },
  );

  if (!grants.has("DrivingSchool:AutoForm:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  if (!drivingSchool) {
    return <LoadingIndicator />;
  }

  return (
    <Create
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Auto-Formular erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/autoForms`);
        },
        onError: (error: Error) => {
          handleError("Fehler beim Erstellen des Auto-Formulars.", error);
        },
      }}
      transform={(data: FieldValues) => ({
        ...data,
        drivingSchoolId,
        isForRegistration: Boolean(data.isForRegistration),
      })}
    >
      <AutoFormForm mode="create" />
    </Create>
  );
}

function EditAutoFormForm() {
  const autoForm = useRecordAndSwitchDrivingSchoolIfNecessary<DrivingSchoolAutoForm>((it) => it.drivingSchoolId ?? "");
  const redirect = useRedirect();

  if (!grants.has("DrivingSchool:AutoForm:edit")) {
    return <AccessDenied />;
  }

  if (!autoForm) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Auto-Formulare"
              backUrl={`/drivingSchools/${autoForm.drivingSchoolId}/autoForms`}
              title={autoForm.name}
            />
          }
        />
      }
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${autoForm.drivingSchoolId}/autoForms`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Speichern.", error);
        },
      }}
    >
      <AutoFormForm mode="edit" />
    </Edit>
  );
}

export function AutoFormForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  const [{ drivingSchoolId }] = useAutovioContext();
  const [existingPdfDeleted, setExistingPdfDeleted] = useState(false);

  function validate(values: FieldValues) {
    const errors: Record<string, string> = {};
    if (!String(values.name ?? "").trim()) {
      errors.name = "Bitte gib einen Titel ein.";
    }
    if (mode === "create" && !values.pdfFile) {
      errors.pdfFile = "Bitte lade eine PDF-Vorlage hoch.";
    }
    if (mode === "edit" && existingPdfDeleted && !values.pdfFile) {
      errors.pdfFile = "Bitte lade eine PDF-Vorlage hoch.";
    }
    return errors;
  }

  const onDeleteExistingPdf = useCallback(() => {
    setExistingPdfDeleted(true);
  }, []);

  return (
    <Form2 validate={validate} warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput label="Titel" source="name" autoFocus={mode === "create"} />
        <TextInput label="Beschreibung" source="description" />
        <_PdfTemplateFileInput
          mode={mode}
          existingPdfDeleted={existingPdfDeleted}
          onDeleteExistingPdf={onDeleteExistingPdf}
        />
        <Box sx={{ mt: 1.5 }}>
          <BooleanInput label={<_RegistrationToggleLabel />} source="isForRegistration" />
        </Box>
        <Row sx={{ pt: 1, justifyContent: "space-between" }}>
          <SaveButton2 />
          {mode === "edit" && grants.has("DrivingSchool:AutoForm:delete") && (
            <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/autoForms`} />
          )}
        </Row>
      </Column>
    </Form2>
  );
}

function _RegistrationToggleLabel() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = !!anchorEl;

  return (
    <Row sx={{ alignItems: "center", gap: 0.5 }}>
      <span>Vertragsdokument: Automatisch bei Anmeldung ausfüllen</span>
      <IconButton
        size="small"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ p: 0.25 }}
        aria-label="Info zur automatischen Befüllung bei Anmeldung"
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 520 }}>
          <Typography variant="body2">
            Dieses Dokument wird den Schülern bei der Online-Anmeldung angezeigt und gehört zur verbindlichen Buchung.
            Nach Abschluss der Anmeldung wird dieses Dokument dann mit den Angaben des Schülers ausgefüllt und als PDF
            am Profil des Schülers gespeichert.
          </Typography>
        </Box>
      </Popover>
    </Row>
  );
}

function _PdfTemplateFileInput(props: {
  mode: "create" | "edit";
  existingPdfDeleted: boolean;
  onDeleteExistingPdf: () => void;
}) {
  const { mode, existingPdfDeleted, onDeleteExistingPdf } = props;
  const {
    field: { onChange, onBlur, value: fileValue },
  } = useInput({ source: "pdfFile" });
  const raRecord = useRecordContext<DrivingSchoolAutoForm>();
  const { dialogProps, openDialog } = useDialog();
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const onDrop = (newFiles: File[]) => {
    onChange(newFiles[0]);
    onBlur();
  };
  const file = _extractPdfFile(fileValue);
  const pdfTemplateBaseFile = raRecord?.pdfTemplateBaseFile;
  const hasExistingPdf = mode === "edit" && !!pdfTemplateBaseFile && !existingPdfDeleted;
  const canEditTemplate = mode === "edit" && (hasExistingPdf || !!file);

  if (hasExistingPdf && !file) {
    return (
      <>
        <Typography variant="caption">PDF-Vorlage</Typography>
        <Box sx={{ position: "relative" }}>
          <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Row sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
              <AttachFileIcon sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }} />
              <Typography
                sx={{ textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {pdfTemplateBaseFile.fileName} ({formatFileSize(pdfTemplateBaseFile.fileSize)})
              </Typography>
            </Row>
            <Row sx={{ alignItems: "center" }}>
              <Button size="small" variant="outlined" onClick={openDialog}>
                Template bearbeiten
              </Button>
              <IconButton onClick={openMenu}>
                <MoreVertIcon />
              </IconButton>
            </Row>
          </Row>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={closeMenu}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                onChange(undefined);
                onBlur();
                onDeleteExistingPdf();
              }}
            >
              PDF löschen
            </MenuItem>
          </Menu>
        </Box>
        {canEditTemplate && <AutoFormTemplateDialog {...dialogProps} autoForm={raRecord} />}
      </>
    );
  }

  const fileName = file?.name;
  const fileSize = file?.size;
  return (
    <>
      <FileInput
        label="PDF-Vorlage"
        source="pdfFile"
        fullWidth
        accept={"application/pdf"}
        options={{ multiple: false, onDrop }}
        placeholder={
          <>
            {!file && mode === "create" && (
              <Typography component="p" sx={{ color: "text.disabled" }}>
                PDF-Datei hier hineinziehen oder
                <br />
                hier klicken, um eine PDF-Datei auszuwählen …
              </Typography>
            )}
            {mode === "edit" && !file && (
              <Typography component="p" sx={{ color: "text.disabled" }}>
                PDF-Datei hier hineinziehen oder hier klicken, um eine PDF-Datei auszuwählen.
              </Typography>
            )}
            {fileName && fileSize !== undefined && (
              <p
                style={{
                  textAlign: "left",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName} ({formatFileSize(fileSize)})
              </p>
            )}
          </>
        }
      />
      {canEditTemplate && (
        <Row>
          <Button size="small" variant="outlined" onClick={openDialog}>
            Template bearbeiten
          </Button>
        </Row>
      )}
      {canEditTemplate && <AutoFormTemplateDialog {...dialogProps} autoForm={raRecord} />}
    </>
  );
}

function _extractPdfFile(value: unknown): File | undefined {
  if (!value) {
    return undefined;
  }
  if (value instanceof File) {
    return value;
  }
  if (Array.isArray(value)) {
    const firstValue = value[0];
    if (firstValue instanceof File) {
      return firstValue;
    }
    if (firstValue && typeof firstValue === "object" && "rawFile" in firstValue && firstValue.rawFile instanceof File) {
      return firstValue.rawFile;
    }
    return undefined;
  }
  if (typeof value === "object" && "rawFile" in value && value.rawFile instanceof File) {
    return value.rawFile;
  }
  return undefined;
}

export const autoFormsResource = (
  <Resource
    key="autoForms"
    name="autoForms"
    list={DefaultList}
    create={CreateAutoFormForm}
    edit={EditAutoFormForm}
    options={{
      label: "Auto-Formulare",
      createTitle: "Neues Auto-Formular",
      noEntriesText: "Noch kein Auto-Formular hinterlegt.",
      noMatchingEntriesText: "Keine passenden Auto-Formulare gefunden.",
    }}
    recordRepresentation={(record: DrivingSchoolAutoForm) => record.name}
  />
);
